import React, { useState } from "react";
import { Button, Card, Checkbox, Label, TextInput } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';
import { apiBaseUrl, authService, userService } from "../../services/main";
import type { FC } from "react";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthContext";

interface AuthResponse {
  data: { token: string, user: any };
}
const SignUpPage: FC = function () {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Inside handleLogin function
  const { login } = useAuth();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { firstName, lastName, mobileNumber, email, password, confirmPassword, acceptTerms } = formData;

    if (!firstName || !lastName || !mobileNumber || !email || !password || !confirmPassword || !acceptTerms) {
      setError("Please fill out all fields and accept the terms.");
      return;
    }

    if (!/^\d{10}$/.test(mobileNumber)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const userDetails = { firstName, lastName, mobileNumber, email, password };

      const response = await userService.createUser(userDetails);
      console.log("Signup successful:", response.data);

      // Show success toast
      toast.success("Account created successfully!");

      // Navigate after a short delay to allow users to see the success message
      setTimeout(() => navigate("/authentication/sign-in", { replace: true }), 3000);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "An unexpected error occurred.";
      setError(errorMessage);
    } finally {
      setLoading(false); // Set loading to false after the submission is complete
    }
  };



  // Handle Google login
  const handleGoogleLogin = async (credentialResponse: any) => {
    const decoded: any = jwtDecode(credentialResponse.credential);
    console.log("Decoded Google User Info:", decoded);

    // Send the Google token to your backend for verification
    try {
      const response: AuthResponse = await authService.authenticate({
        googleToken: credentialResponse.credential,
      });

      // Save token and user info in session storage
      sessionStorage.setItem("token", response.data.token);
      sessionStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Login successful!");
      login(); // Set the user as authenticated
      navigate("/");
      // Redirect to the home page
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(`Google login failed: ${errorMessage}`);
    }
  };
  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">

      <Card
        horizontal
        imgSrc="https://img.freepik.com/free-photo/office-workers-using-finance-graphs_23-2150408668.jpg?ga=GA1.1.1506045701.1748711164&semt=ais_hybrid&w=740"
        imgAlt=""
        className="w-full md:max-w-[1024px] md:[&>*]:w-full md:[&>*]:p-16 [&>img]:hidden md:[&>img]:w-96 md:[&>img]:p-0 lg:[&>img]:block"
      >
        <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-3xl">Create a Free Account</h1>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { field: "firstName", placeholder: "Your First Name" },
            { field: "lastName", placeholder: "Your Last Name" },
            { field: "mobileNumber", placeholder: "10-digit Mobile Number" },
            { field: "email", placeholder: "Your Email Address" },
            { field: "password", placeholder: "Enter Password" },
            { field: "confirmPassword", placeholder: "Confirm Password" },
          ].map(({ field, placeholder }) => (
            <div key={field} className="flex flex-col gap-y-3">
              <Label htmlFor={field}>
                {field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                <span className="text-red-500"> *</span> {/* Add the red asterisk here */}
              </Label>
              <TextInput
                id={field}
                name={field}
                placeholder={placeholder}
                type={field.includes("password") ? "password" : field === "email" ? "email" : "text"}
                value={formData[field as keyof typeof formData] as string}
                onChange={handleChange}
                required
              />
            </div>

          ))}
          <div className="flex items-center gap-x-3 sm:col-span-2">
            <Checkbox id="acceptTerms" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} />
            <Label htmlFor="acceptTerms">
              I accept the&nbsp;
              <a href="#" className="text-primary-700 dark:text-primary-200">
                Terms and Conditions
              </a>
            </Label>
          </div>
          {error && <div className="text-red-500 text-sm sm:col-span-2">{error}</div>}
          <div className="sm:col-span-2">
            <Button type="submit" className="w-full lg:w-auto" disabled={loading}>
              {loading ? "Creating..." : "Create account"} {/* Show loading text */}
            </Button>
          </div>
          <hr className=" border-gray-300 w-auto sm:col-span-2" />


        </form>
        <div className="w-full">
          <GoogleOAuthProvider clientId="734558150273-k68q9qj6kfdrjh4h1t6538uvjkm7mu4e.apps.googleusercontent.com">
            <div>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => console.log('Login Failed')}
              />
            </div>
          </GoogleOAuthProvider>
          <p className="text-sm text-gray-500 dark:text-gray-300 sm:col-span-2 mt-4">
            Already have an account?&nbsp;
            <button onClick={() => navigate("/authentication/sign-in")} className="text-primary-600 dark:text-primary-200">
              Login here
            </button>
          </p>
        </div>
      </Card>

    </div>
  );
};

export default SignUpPage;
